import {Injectable, Injector} from "@angular/core";
import {ReportError} from "app/app.service";

import {BaseListService} from "app/services/domain/base.service";
import * as Models from "app/services/proxy/model/models";

import {Lookup} from "framework/services/utils.service";

@Injectable()
export class AppOnboardingApplicationService extends BaseListService<Models.AppSecopsApplicationListRequest, Models.BatchRequest, Models.AppSecopsApplication>
{
    private readonly m_applicationsByTenant: Lookup<Models.AppSecopsApplication[]>      = {};
    private readonly m_fetchingByTenant: Lookup<Promise<Models.AppSecopsApplication[]>> = {};

    constructor(inj: Injector)
    {
        super(inj, Models.AppSecopsApplication);
    }

    @ReportError
    public list(listReq: Models.AppSecopsApplicationListRequest): Promise<Models.ListResponse>
    {
        return this.domain.apis.appSecOps.listApplication(listReq);
    }

    protected async getBatchResponseImpl(req: Models.BatchRequest): Promise<Models.AppSecopsApplicationBatchResponse>
    {
        const recordIdBatchRequest = Models.BatchRequest.newInstance(req);

        return this.domain.apis.appSecOps.batchApplication(recordIdBatchRequest);
    }

    protected createBatchRequest(listReq: Models.AppSecopsApplicationListRequest,
                                 ids: string[]): Models.BatchRequest
    {
        return Models.BatchRequest.newInstance({ids: ids});
    }

    public async mappedPortfolio(applications: Models.AppSecopsApplication[],
                                 tenantId: string[]): Promise<Lookup<Models.AppSecopsPortfolioHierarchyResponse>>
    {
        let applicationPortfolioLookup: Lookup<Models.AppSecopsPortfolioHierarchyResponse> = {};
        const portfolioHierarchy                                                           = await this.app.domain.appOnboardingPortfolio.getPortfolioHierarchy(tenantId[0]);
        if (portfolioHierarchy?.length)
        {
            applications.forEach(application =>
                                 {
                                     applicationPortfolioLookup[application.application_id] = portfolioHierarchy.find(portfolio => portfolio.portfolio_id === application.portfolio_id);
                                 });
        }
        return applicationPortfolioLookup;
    }

    public async getAllForTenant(tenantId: string): Promise<Models.AppSecopsApplication[]>
    {
        if (this.m_applicationsByTenant[tenantId])
        {
            return this.m_applicationsByTenant[tenantId];
        }

        if (!this.m_fetchingByTenant[tenantId])
        {
            this.m_fetchingByTenant[tenantId] = (async () =>
            {
                try
                {
                    const ids = await this.listAll(Models.AppSecopsApplicationListRequest.newInstance({
                                                                                                          filters: Models.AppSecopsApplicationFilterBase.newInstance({
                                                                                                                                                                         tenant_id: tenantId
                                                                                                                                                                     })
                                                                                                      }));
                    if (ids.length)
                    {
                        this.m_applicationsByTenant[tenantId] = await this.batchAll(ids);
                    }
                }
                finally
                {
                    delete this.m_fetchingByTenant[tenantId];
                }

                return this.m_applicationsByTenant[tenantId];
            })();
        }

        return this.m_fetchingByTenant[tenantId];
    }

    public resetTenantCache(tenantId: string)
    {
        this.m_applicationsByTenant[tenantId] = null;
    }

    public async getAllApplicationNamesForTenant(tenantId: string): Promise<string[]>
    {
        const allApp = await this.getAllForTenant(tenantId);
        return allApp?.map(application => application.name);
    }

    public async getMappedWorkloads(applications: Models.AppSecopsApplication[],
                                    tenantId: string): Promise<Lookup<string[]>>
    {
        const workloadLookup: Lookup<string[]> = {};
        const allWorkloads                     = await this.app.domain.workloadsList.getAllForTenant(tenantId);
        applications.forEach(app =>
                             {
                                 workloadLookup[app.application_id] = (app.workload_ids ?? [])
                                     .map(id => allWorkloads?.find(w => w.sysId === id)?.name)
                                     .filter((name): name is string => !!name);
                             });
        return workloadLookup;
    }

    public getApplicationById(portfolioHierarchy: Models.AppSecopsPortfolioHierarchyResponse[],
                              applicationId: string): Models.AppSecopsDashboardApplicationDetails
    {
        let application: Models.AppSecopsDashboardApplicationDetails = null;
        for (const portfolio of portfolioHierarchy)
        {
            application = portfolio.applications.find(app => app.application_id === applicationId);
            if (application) break;
        }
        return application;
    }

    public registerApplicationWorkloadFeatureFlag(): void
    {
        void this.domain.featureFlagsRequester.registerFeatureFlagRequest(Models.FeatureFlagCategory.FEATURE,
                                                                          Models.SecurityGovernanceFeatureFlags.segn_2603_a_application_to_workload);
    }
}
